import * as styles from '../FirstResponder.module.css';
import {
  GUARD_SKIP_LABELS,
  LiveDiagnostics,
  LiveDiagnosticValue,
  LiveGuardSkip,
} from '../LivePreview/liveViewModel';
import { useReactiveVar } from '@apollo/client';
import { UserRoleEnum } from '@/types/graphql';
import { currentAdminVar } from '@/util/apollo/cache';

/*
 * Canonical presentation component (GRAVY-10373 convergence, Phase 1 extraction).
 * Extracted verbatim from LiveConsole.tsx's Diagnostics & safety <details> section.
 */

// null = missing evidence -> the word "Unknown", NEVER 0. A backend-proven zero renders 0.
const diagValue = (v: LiveDiagnosticValue): string =>
  v === null ? 'Unknown' : String(v);

/*
 * Guard skips: the offer attempts the runner declined to make, per guard. Same null rule as every
 * other diagnostic — null is "the mode had no attempts, so nothing is known". It says so in words
 * (GRAVY-10816 walk, W1): "Unknown" reads as a broken count to a tester, and a shadow console has
 * no attempts by design, not by omission. A measured list with nothing above zero renders the
 * proven "none". A guard that stopped
 * nothing is dropped from the line rather than printed as a zero, so the sentence stays readable.
 */
const guardSkipText = (skips: LiveGuardSkip[] | null): string => {
  if (skips === null) return 'no attempts recorded in this mode yet';
  const stopped = skips.filter((s) => s.count !== 0);
  if (stopped.length === 0) return 'none';
  return stopped
    .map((s) => `${GUARD_SKIP_LABELS[s.reason]} ${diagValue(s.count)}`)
    .join(' · ');
};

type Props = {
  diagnostics: LiveDiagnostics;
  // F11: "Run one tick now" — lets an operator tick a review app that has no scheduler, and
  // doubles as the demo control. The caller (Connected/index.tsx for real data; LivePreview/
  // index.tsx with a no-op stub for the dev-only preview) owns the actual request, the pending
  // flag and the result text — this component only renders what it's given and forwards clicks.
  onRunTick: () => void;
  runTickPending: boolean;
  runTickResult: string | null;
};

const DiagnosticsSection = ({
  diagnostics,
  onRunTick,
  runTickPending,
  runTickResult,
}: Props) => {
  // A GravyWork-internal control, TenantAdmin only. Customer (client) admins and helpdesk admins
  // never see it; the console route itself is reachable by helpdesk admins too (PrivateRoute's
  // UserRoleEnum.TENANT_ADMIN bucket covers both), so this finer-grained check is what actually
  // excludes them. The gate lives here, on just this block, rather than hiding the whole
  // Diagnostics & safety section — everyone who can reach the console can still read
  // diagnostics, only the control to run one is restricted.
  //
  // Keyed on `user.roleId`, the same field PrivateRoute and PermissionComponent use, and
  // deliberately NOT useAuth's `currentAdminIsTenantAdmin` (review round 1): that compares
  // `TenantAdmin.role` — graphy's TenantRole column, where ADMIN is 2 — against `Role.TENANT_ADMIN`,
  // which is 1, so it is never true for a real tenant admin and this button never rendered.
  // WorkerV2/index.tsx keys its badge card on the same field for the same reason.
  const currentAdmin = useReactiveVar(currentAdminVar);
  const isTenantAdminRole =
    currentAdmin?.user?.roleId === UserRoleEnum.TENANT_ADMIN;

  return (
    <details className={styles.collapse} data-testid="flp-diagnostics">
      <summary>Diagnostics &amp; safety</summary>
      <div
        className={styles.section}
        style={{ borderTop: '1px solid var(--n-lightest)' }}
      >
        {isTenantAdminRole && (
          <div style={{ margin: '0 0 16px' }}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnOutline}`}
              data-testid="flp-run-tick"
              disabled={runTickPending}
              onClick={onRunTick}
            >
              Run one tick now
            </button>
            {runTickResult && (
              <p
                className={`${styles.muted} ${styles.small}`}
                style={{ margin: '8px 0 0' }}
                data-testid="flp-run-tick-result"
              >
                {runTickResult}
              </p>
            )}
          </div>
        )}
        <div className={`${styles.sig} ${styles.sigFlush}`}>
          <div>
            <div className={styles.k}>Offers created</div>
            <div className={styles.v} data-testid="flp-diag-offers">
              {diagValue(diagnostics.offersCreated)}
            </div>
          </div>
          <div>
            <div className={styles.k}>Push sent</div>
            <div className={styles.v} data-testid="flp-diag-push">
              {diagValue(diagnostics.pushSent)}
            </div>
          </div>
          <div>
            <div className={styles.k}>SMS sent</div>
            <div className={styles.v} data-testid="flp-diag-sms">
              {diagValue(diagnostics.smsSent)}
            </div>
          </div>
          <div>
            <div className={styles.k}>Observed modes</div>
            <div className={styles.v}>
              {diagnostics.observedModes.join(', ') || '—'}
            </div>
          </div>
        </div>
        <p
          className={`${styles.muted} ${styles.small}`}
          style={{ margin: '16px 0 0' }}
          data-testid="flp-diag-guard-skips"
        >
          Offer attempts skipped by a guard:{' '}
          {guardSkipText(diagnostics.guardSkips)}
        </p>
        {diagnostics.systemAlerts.length > 0 && (
          <ul
            style={{ margin: '16px 0 0', paddingLeft: 18 }}
            data-testid="flp-system-alerts"
          >
            {diagnostics.systemAlerts.map((a) => (
              <li key={a.key}>
                <span className={styles.strong}>{a.label}</span>{' '}
                <span className={`${styles.muted} ${styles.small}`}>
                  {a.detail}
                </span>
              </li>
            ))}
          </ul>
        )}
        {diagnostics.note && (
          <p
            className={`${styles.muted} ${styles.small}`}
            style={{ margin: '16px 0 0' }}
          >
            {diagnostics.note}
          </p>
        )}
      </div>
    </details>
  );
};

export default DiagnosticsSection;
